// CTC prefix beam search (Graves 2012) with trie LM, word n-gram LM, and
// Gaussian-weighted per-word confidence — Structure-of-Arrays, tuned for the JIT
// and the allocator. Output is bit-exact to the original object-based decoder.
//
// Each frame runs a fixed pipeline (see ctcBeamStep):
//   collectCandidates → expandBeams → selectTopB → pruneFrame → compactSurvivors → evictStablePrefix
//
// Design notes:
//   • Beams are array indices into parallel typed arrays, not objects — no
//     hidden-class churn, no GC of the hundreds of hypotheses born each frame.
//   • Prefix dedup is a pooled token tree keyed by an O(1) (parent,token) edge
//     map; no per-frame Map/sort/closure.
//   • One V-scan/frame finds the max and the top-K together; per-token logprobs
//     are `logit - max`, computed on demand for the ~35 tokens actually touched.
//   • Committed per-word scores live in a refcounted cons pool, so inheriting a
//     parent's words is an O(1) pointer copy, not an O(words) array copy.
//   • Dead branches are pruned every frame and the settled leading prefix is
//     evicted (root shifting), so live state is O(beamWidth × divergence window),
//     not O(transcript) — safe for arbitrarily long streaming. Every pool reuses
//     a freelist, so steady-state allocation is ~zero.

const NEG_INF = -Infinity;

function logAdd(a, b) {
  if (a === NEG_INF) return b;
  if (b === NEG_INF) return a;
  return a > b ? a + Math.log1p(Math.exp(b - a)) : b + Math.log1p(Math.exp(a - b));
}

// Per-token log-prob: (logit − frame max), rounded to float32. The original
// decoder stored these in a Float32Array, so rounding here keeps scoring
// bit-for-bit identical (`logits` is already a Float32Array, so the operands are
// exact; only their difference needs rounding).
function frameLogProb(logits, idx, max) {
  return Math.fround(logits[idx] - max);
}

// ════════════════════════════════ trie LM ═══════════════════════════════════
// The passage trie (object graph with Map children) flattened to typed arrays so
// lookups are array reads, not Map hashing. CSR layout: node i owns
// child{Tok,Node}[childStart[i]..childStart[i+1]). Few children per node (BPE) ⇒
// linear scan; the root is the one high-fan-out node and gets a dense
// token→child table. Node 0 is the root; -1 means off-trie.

function flattenTrie(trie, V) {
  const nodes = [trie.root];
  const idOf = new Map([[trie.root, 0]]);
  for (let i = 0; i < nodes.length; i++) {
    for (const child of nodes[i].children.values()) {
      if (!idOf.has(child)) { idOf.set(child, nodes.length); nodes.push(child); }
    }
  }
  const N = nodes.length;
  const isWordEnd = new Uint8Array(N);
  const wordId = new Int32Array(N).fill(-1);
  let total = 0;
  for (const n of nodes) total += n.children.size;
  const childStart = new Int32Array(N + 1);
  const childTok = new Int32Array(total);
  const childNode = new Int32Array(total);
  const rootChild = new Int32Array(V).fill(-1);
  let off = 0;
  for (let i = 0; i < N; i++) {
    childStart[i] = off;
    const n = nodes[i];
    isWordEnd[i] = n.isWordEnd ? 1 : 0;
    if (n.wordId !== undefined) wordId[i] = n.wordId;
    for (const [tok, child] of n.children) { // Map keeps insertion order ⇒ candidate order preserved
      const cid = idOf.get(child);
      childTok[off] = tok; childNode[off] = cid;
      if (i === 0) rootChild[tok] = cid;
      off++;
    }
  }
  childStart[N] = off;
  return { childStart, childTok, childNode, isWordEnd, wordId, rootChild };
}

// Canonical child of trie node `id` for token `c`, or -1.
function trieChild(ft, id, c) {
  if (id === 0) return ft.rootChild[c];
  for (let e = ft.childStart[id]; e < ft.childStart[id + 1]; e++) {
    if (ft.childTok[e] === c) return ft.childNode[e];
  }
  return -1;
}

// ═══════════════════════ beam buffer (Structure-of-Arrays) ═══════════════════
// One column per beam field; a "beam" is just a row index.

function makeBeams(cap) {
  return {
    cap,
    node:      new Int32Array(cap),   // prefix tree node index
    logPb:     new Float64Array(cap), // log-prob of paths ending in blank
    logPnb:    new Float64Array(cap), // log-prob of paths ending non-blank
    logLm:     new Float64Array(cap), // accumulated LM bias
    lastToken: new Int32Array(cap),
    trieNode:  new Int32Array(cap),   // flat trie node id (-1 = off-trie)
    committed: new Int32Array(cap),   // cons-list index of committed words (-1 = none)
    curSum:    new Float64Array(cap), // in-progress word: Σ weighted log-prob
    curCount:  new Int32Array(cap),   // in-progress word: token count
    wh0:       new Uint16Array(cap),  // n-gram word-history slots — two slots
    wh1:       new Uint16Array(cap),  // because ngram_lm.js caps context at MAX_CONTEXT = 2
    whLen:     new Uint8Array(cap),
    score:     new Float64Array(cap), // ranking score = logAdd(logPb,logPnb)+logLm
  };
}

function growBeams(b, need) {
  let cap = b.cap;
  while (cap < need) cap *= 2;
  const n = makeBeams(cap);
  n.node.set(b.node); n.logPb.set(b.logPb); n.logPnb.set(b.logPnb); n.logLm.set(b.logLm);
  n.lastToken.set(b.lastToken); n.trieNode.set(b.trieNode); n.committed.set(b.committed);
  n.curSum.set(b.curSum); n.curCount.set(b.curCount);
  n.wh0.set(b.wh0); n.wh1.set(b.wh1); n.whLen.set(b.whLen); n.score.set(b.score);
  return n;
}

// Copies `score` too, though it's dead once survivors are promoted — selectTopB
// recomputes it from logPb/logPnb/logLm every frame.
function copyRow(dst, di, src, si) {
  dst.node[di] = src.node[si]; dst.logPb[di] = src.logPb[si]; dst.logPnb[di] = src.logPnb[si];
  dst.logLm[di] = src.logLm[si]; dst.lastToken[di] = src.lastToken[si]; dst.trieNode[di] = src.trieNode[si];
  dst.committed[di] = src.committed[si]; dst.curSum[di] = src.curSum[si]; dst.curCount[di] = src.curCount[si];
  dst.wh0[di] = src.wh0[si]; dst.wh1[di] = src.wh1[si]; dst.whLen[di] = src.whLen[si]; dst.score[di] = src.score[si];
}

// ═══════════════════════ committed-word cons pool ════════════════════════════
// Each committed word is a {sum, count, prev} cell; a beam holds the head of its
// chain. Refcounted so a cell frees when no beam row and no child cell points at
// it, bounding the pool to the live survivors' chains.

function growCells(state) {
  const cap = state.cellCap === 0 ? 256 : state.cellCap * 2;
  const cellSum = new Float32Array(cap), cellCount = new Int32Array(cap);
  const cellPrev = new Int32Array(cap), cellRef = new Int32Array(cap);
  cellSum.set(state.cellSum); cellCount.set(state.cellCount);
  cellPrev.set(state.cellPrev); cellRef.set(state.cellRef);
  state.cellSum = cellSum; state.cellCount = cellCount; state.cellPrev = cellPrev; state.cellRef = cellRef;
  state.cellCap = cap;
}

function allocCell(state) {
  if (state.cellFree.length > 0) return state.cellFree.pop();
  if (state.cellTop >= state.cellCap) growCells(state);
  return state.cellTop++;
}

// Drop one reference to cell `c`; free it (and release its predecessor) when the
// refcount hits zero, cascading down the chain.
function releaseCell(state, c) {
  while (c !== -1 && --state.cellRef[c] === 0) {
    const p = state.cellPrev[c];
    state.cellFree.push(c);
    c = p;
  }
}

// ═══════════════════════════ token tree (prefix dedup) ═══════════════════════

function growTree(state, need) {
  let cap = state.tCap;
  while (cap < need) cap *= 2;
  const tParent = new Int32Array(cap), tToken = new Int32Array(cap);
  const tBeamSlot = new Int32Array(cap), tBeamStamp = new Int32Array(cap);
  const tChildCount = new Int32Array(cap), tLiveStamp = new Int32Array(cap), tFreed = new Uint8Array(cap);
  tParent.set(state.tParent); tToken.set(state.tToken);
  tBeamSlot.set(state.tBeamSlot); tBeamStamp.set(state.tBeamStamp);
  tChildCount.set(state.tChildCount); tLiveStamp.set(state.tLiveStamp); tFreed.set(state.tFreed);
  state.tParent = tParent; state.tToken = tToken;
  state.tBeamSlot = tBeamSlot; state.tBeamStamp = tBeamStamp;
  state.tChildCount = tChildCount; state.tLiveStamp = tLiveStamp; state.tFreed = tFreed;
  state.tCap = cap;
}

// Free a dead leaf and any ancestors it leaves childless, stopping at a live node
// (on a survivor path / still has children), the root, or an already-freed node.
function pruneTree(state, node, stamp) {
  while (node !== state.root && !state.tFreed[node] &&
         state.tLiveStamp[node] !== stamp && state.tChildCount[node] === 0) {
    const p = state.tParent[node];
    state.edge.delete(p * state.edgeStride + state.tToken[node]);
    state.tFreed[node] = 1;
    state.tChildCount[p]--;
    state.treeFree.push(node);
    node = p;
  }
}

function pushEmitted(state, token) {
  if (state.emittedLen >= state.emitted.length) {
    const e = new Int32Array(state.emitted.length * 2); e.set(state.emitted); state.emitted = e;
  }
  state.emitted[state.emittedLen++] = token;
}

// ════════════════════════════ word-history (n-gram) ══════════════════════════

// Push a completed word id into row `ni`'s history (mirrors ngram_lm's
// pushWordHistory over the flat columns).
function pushWordHistoryRow(beams, ni, wordId, contextLen) {
  const len = beams.whLen[ni];
  if (len < contextLen) {
    if (len === 0) beams.wh0[ni] = wordId; else beams.wh1[ni] = wordId;
    beams.whLen[ni] = len + 1;
  } else if (contextLen === 2) {
    beams.wh0[ni] = beams.wh1[ni];
    beams.wh1[ni] = wordId;
  } else if (contextLen === 1) {
    beams.wh0[ni] = wordId;
  }
}

// Initialize next-beam row `ni` as a child of cur[srcBi]. newToken === -1 means
// blank/repeat (word state copied unchanged); a real token applies the Gaussian-
// weighted contribution and, when word-initial, commits the parent's in-progress
// word as a refcounted cons cell.
function initRow(state, ni, srcBi, destNode, trieNode, logLm, lastToken, newToken, completedWordId, off, gauss) {
  const { cur, next, isWordInitial, ngramContextLen } = state;
  next.node[ni] = destNode;
  next.logPb[ni] = NEG_INF;
  next.logPnb[ni] = NEG_INF;
  next.trieNode[ni] = trieNode;
  next.logLm[ni] = logLm;
  next.lastToken[ni] = lastToken;
  next.curSum[ni] = cur.curSum[srcBi];
  next.curCount[ni] = cur.curCount[srcBi];
  next.wh0[ni] = cur.wh0[srcBi];
  next.wh1[ni] = cur.wh1[srcBi];
  next.whLen[ni] = cur.whLen[srcBi];

  if (completedWordId >= 0 && ngramContextLen > 0) {
    pushWordHistoryRow(next, ni, completedWordId, ngramContextLen);
  }

  // Refcount invariant: every beam row holds exactly one reference to its
  // committed-chain head, and a cell's refcount = (rows pointing at it) + (cells
  // whose prev points at it). The two cellRef bumps below maintain it.
  const srcCommitted = cur.committed[srcBi];
  let committed = srcCommitted; // default: inherit the parent's chain
  if (newToken !== -1) {
    const weighted = frameLogProb(state.frameLogits, off + newToken, state.frameMax) + gauss;
    if (isWordInitial[newToken]) {
      if (cur.curCount[srcBi] > 0) { // commit the parent's in-progress word
        const cell = allocCell(state);
        state.cellSum[cell] = cur.curSum[srcBi]; // Float32 store (matches original wordScores precision)
        state.cellCount[cell] = cur.curCount[srcBi];
        state.cellPrev[cell] = srcCommitted;
        state.cellRef[cell] = 1;                                // referenced by this row
        if (srcCommitted !== -1) state.cellRef[srcCommitted]++; // referenced by the new cell
        committed = cell;
      }
      next.curSum[ni] = weighted;
      next.curCount[ni] = 1;
    } else {
      next.curSum[ni] = cur.curSum[srcBi] + weighted;
      next.curCount[ni] = cur.curCount[srcBi] + 1;
    }
  }
  next.committed[ni] = committed;
  if (committed === srcCommitted && srcCommitted !== -1) state.cellRef[srcCommitted]++; // row references inherited chain
}

// ══════════════════════════════ per-frame phases ═════════════════════════════

// Frame's candidate set + max. Candidates = every trie continuation from an
// active beam, plus the global top-K non-blank tokens by logit (one V-scan, which
// also yields the frame max). Fills state.candidates, returns the count, and
// stashes the max on state.frameMax.
//
// Admissible prune: once the beam is saturated, a top-K token whose logit is
// below floor = blankLogit − beamSpread − maxLmBonus − pruneSafety can't out-rank
// the worst survivor as a single extension (so it never becomes a beam on its
// own). It can still prefix-merge from up to beamWidth parents, lifting its
// combined mass by ≤ log(beamWidth) nats, so the effective margin is
// pruneSafety − log(beamWidth): the top-B — hence the decoded tokens — are
// provably unchanged as long as pruneSafety > log(beamWidth) (~1.4 at beam 4),
// and word scores move by < e^-(pruneSafety − log beamWidth) (≈6e-8 at the
// default 18, beam 4). Tokens below `floor` skip the top-K maintenance, so on
// blank-dominated frames (the bulk of real CTC) the scan collapses to ~a plain
// max. Set opts.pruneSafety = Infinity to disable.
function collectCandidates(state, logits, off) {
  const { V, blankId, topK, flatTrie: ft, cur, curCount, seen, candidates, topScores, topIds } = state;
  let nCand = 0;
  for (let bi = 0; bi < curCount; bi++) {
    const tn = cur.trieNode[bi];
    if (tn === -1) continue;
    for (let e = ft.childStart[tn]; e < ft.childStart[tn + 1]; e++) {
      const id = ft.childTok[e];
      if (!seen[id]) { seen[id] = 1; candidates[nCand++] = id; }
    }
  }
  topScores.fill(NEG_INF);
  topIds.fill(-1);
  // Admissible prune floor. Gate on a saturated beam: only with ≥ beamWidth
  // blank/repeat continuations is the worst survivor guaranteed to crowd out a
  // below-floor token, making the top-B (and decoded tokens) provably unchanged;
  // until then keep every candidate. maxLmBonus is the largest bonus an extension
  // can add — max(lmBeta, 0) for an on-trie word completion, 0 with no LM — and
  // relies on ngramLm.score() ≤ 0 and lmAlpha ≤ 0 (a penalty), as configured.
  // cur.score is rank-ordered (cur[0] best, cur[curCount-1] worst).
  const maxLmBonus = state.useLm ? Math.max(state.lmBeta, 0) : 0;
  const spread = cur.score[0] - cur.score[curCount - 1];
  const floor = curCount >= state.beamWidth
    ? logits[off + blankId] - spread - maxLmBonus - state.pruneSafety
    : NEG_INF;
  let worstSlot = 0;
  let maxAll = NEG_INF;
  let pruned = 0;
  for (let v = 0; v < V; v++) {
    const g = logits[off + v];
    if (g > maxAll) maxAll = g;
    if (g < floor) { pruned++; continue; }
    if (v === blankId || seen[v]) continue;
    if (g > topScores[worstSlot]) {
      topScores[worstSlot] = g;
      topIds[worstSlot] = v;
      let worst = topScores[0];
      worstSlot = 0;
      for (let i = 1; i < topK; i++) if (topScores[i] < worst) { worst = topScores[i]; worstSlot = i; }
    }
  }
  for (let i = 0; i < topK; i++) {
    const id = topIds[i];
    if (id !== -1 && !seen[id]) { seen[id] = 1; candidates[nCand++] = id; }
  }
  for (let i = 0; i < nCand; i++) seen[candidates[i]] = 0;
  state.prunedTokens += pruned;
  state.frameMax = maxAll;
  return nCand;
}

// Grow the next-beam buffer + token tree to hold this frame's worst case.
function ensureFrameCapacity(state, maxNew) {
  if (maxNew > state.next.cap) {
    state.next = growBeams(state.next, maxNew);
    if (state.survMark.length < state.next.cap) state.survMark = new Int32Array(state.next.cap);
  }
  if (state.tCount + maxNew > state.tCap) growTree(state, state.tCount + maxNew);
}

// Expand every current beam into the next-beam buffer: blank, repeat, and one
// extension per candidate token. Returns the next-beam count.
function expandBeams(state, logits, off, nCand, gauss, blankLp, stamp) {
  const { cur, curCount, candidates, edgeStride, isWordInitial, flatTrie: ft,
          useLm, ngramLm, lmAlpha, lmBeta, frameMax } = state;
  const next = state.next;
  let nextCount = 0;
  let tCount = state.tCount;
  state.created.length = 0;

  for (let bi = 0; bi < curCount; bi++) {
    const bLogPb = cur.logPb[bi];
    const bLogPnb = cur.logPnb[bi];
    const bNode = cur.node[bi];
    const bTrie = cur.trieNode[bi];
    const bLogLm = cur.logLm[bi];
    const bLast = cur.lastToken[bi];
    const logTotal = logAdd(bLogPb, bLogPnb);

    // blank + repeat both keep the prefix (target bNode): blank always creates
    // the row; the repeat (if any) reuses it.
    let ni;
    if (state.tBeamStamp[bNode] === stamp) {
      ni = state.tBeamSlot[bNode];
    } else {
      ni = nextCount++;
      initRow(state, ni, bi, bNode, bTrie, bLogLm, bLast, -1, -1, off, gauss);
      state.tBeamSlot[bNode] = ni; state.tBeamStamp[bNode] = stamp;
    }
    next.logPb[ni] = logAdd(next.logPb[ni], logTotal + blankLp);
    if (bLast !== -1) next.logPnb[ni] = logAdd(next.logPnb[ni], bLogPnb + frameLogProb(logits, off + bLast, frameMax));

    for (let ci = 0; ci < nCand; ci++) {
      const c = candidates[ci];
      const lp = frameLogProb(logits, off + c, frameMax);
      const fromBlank = bLogPb + lp;
      const fromNonBlank = c !== bLast ? bLogPnb + lp : NEG_INF;
      const ctcScore = logAdd(fromBlank, fromNonBlank);
      if (ctcScore === NEG_INF) continue;

      // trie LM: word-completion bonus / off-trie penalty, and the destination
      // trie node carried by the new prefix.
      let newTrieNode = -1;
      let lmDelta = 0;
      let completedWordId = -1;
      if (useLm) {
        if (isWordInitial[c]) {
          if (bTrie !== -1 && ft.isWordEnd[bTrie]) {
            lmDelta += lmBeta;
            if (ngramLm && ft.wordId[bTrie] !== -1) {
              completedWordId = ft.wordId[bTrie];
              // word-history is at most 2 ids (ngram_lm MAX_CONTEXT = 2); this
              // runs only at word completions, so a fresh 2-element view is free.
              lmDelta += ngramLm.score(Uint16Array.of(cur.wh0[bi], cur.wh1[bi]), cur.whLen[bi], completedWordId);
            }
          }
          newTrieNode = ft.rootChild[c];
        } else {
          newTrieNode = bTrie !== -1 ? trieChild(ft, bTrie, c) : -1;
        }
        if (newTrieNode === -1) lmDelta += lmAlpha;
      }

      // canonical tree node for this prefix (O(1) edge map; reuse a freed slot)
      const ek = bNode * edgeStride + c;
      let destNode = state.edge.get(ek);
      if (destNode === undefined) {
        destNode = state.treeFree.length > 0 ? state.treeFree.pop() : tCount++;
        state.tParent[destNode] = bNode;
        state.tToken[destNode] = c;
        state.tBeamStamp[destNode] = -1; // stale < stamp (monotonic) ⇒ treated as new
        state.tChildCount[destNode] = 0;
        state.tFreed[destNode] = 0;
        state.tChildCount[bNode]++;
        state.created.push(destNode);
        state.edge.set(ek, destNode);
      }

      let di;
      if (state.tBeamStamp[destNode] === stamp) {
        di = state.tBeamSlot[destNode];
      } else {
        di = nextCount++;
        initRow(state, di, bi, destNode, newTrieNode, bLogLm + lmDelta, c, c, completedWordId, off, gauss);
        state.tBeamSlot[destNode] = di; state.tBeamStamp[destNode] = stamp;
      }
      next.logPnb[di] = logAdd(next.logPnb[di], ctcScore);
    }
  }

  state.tCount = tCount;
  return nextCount;
}

// Score the next beams and select the top `beamWidth` into state.topIdx, in
// score-descending order with ties broken by lower index — matching a stable
// sort + take-first-B exactly. Returns how many were kept.
function selectTopB(state, nextCount) {
  const { next, beamWidth, topIdx } = state;
  for (let ni = 0; ni < nextCount; ni++) {
    next.score[ni] = logAdd(next.logPb[ni], next.logPnb[ni]) + next.logLm[ni];
  }
  let topN = 0;
  for (let ni = 0; ni < nextCount; ni++) {
    const s = next.score[ni];
    if (topN < beamWidth) {
      let j = topN;
      while (j > 0 && next.score[topIdx[j - 1]] < s) { topIdx[j] = topIdx[j - 1]; j--; }
      topIdx[j] = ni; topN++;
    } else if (s > next.score[topIdx[beamWidth - 1]]) {
      let j = beamWidth - 1;
      while (j > 0 && next.score[topIdx[j - 1]] < s) { topIdx[j] = topIdx[j - 1]; j--; }
      topIdx[j] = ni;
    }
  }
  return topN;
}

// Free everything the survivors don't need: word-score cells held by dropped
// next-rows and by the (now superseded) source rows, and the dead tree branches.
// Survivor nodes are marked live so the prune cascade stops at them; ancestors
// are kept automatically by their nonzero child counts (no full-path walk).
function pruneFrame(state, nextCount, topN, stamp) {
  const { cur, next, curCount, topIdx, survMark } = state;
  for (let j = 0; j < topN; j++) {
    const si = topIdx[j];
    survMark[si] = stamp;
    state.tLiveStamp[next.node[si]] = stamp;
  }
  for (let ni = 0; ni < nextCount; ni++) {
    if (survMark[ni] !== stamp) releaseCell(state, next.committed[ni]);
  }
  for (let bi = 0; bi < curCount; bi++) releaseCell(state, cur.committed[bi]);
  for (let k = 0; k < state.created.length; k++) pruneTree(state, state.created[k], stamp);
  for (let bi = 0; bi < curCount; bi++) pruneTree(state, cur.node[bi], stamp);
}

// Promote the top-N survivors into the current beam buffer (read only after the
// source rows above are fully consumed).
function compactSurvivors(state, topN) {
  if (topN > state.cur.cap) state.cur = growBeams(state.cur, topN);
  const { cur, next, topIdx } = state;
  for (let j = 0; j < topN; j++) copyRow(cur, j, next, topIdx[j]);
  state.curCount = topN;
}

// Evict the settled leading prefix (root shifting). While the root has exactly
// one child and no surviving beam ends at it, that child is shared by every
// hypothesis and permanent (CTC prefixes only grow): emit its token and advance
// the root. The single child lies on beam 0's path, so walk that path once and
// march the root down it — keeping the live tree O(divergence window).
function evictStablePrefix(state) {
  const { cur, curCount, edgeStride } = state;
  if (curCount === 0 || state.tChildCount[state.root] !== 1) return;

  // pscr holds beam 0's path stored leaf→root (pscr[0] = leaf, pscr[pathLen-1] =
  // root's child); we consume it root→leaf (idx high→low) to march the root down.
  let pscr = state.pathScratch;
  let pathLen = 0;
  for (let n = cur.node[0]; n !== state.root; n = state.tParent[n]) {
    if (pathLen >= pscr.length) { const p2 = new Int32Array(pscr.length * 2); p2.set(pscr); pscr = state.pathScratch = p2; }
    pscr[pathLen++] = n;
  }
  let root = state.root;
  for (let idx = pathLen - 1; idx >= 0 && state.tChildCount[root] === 1; idx--) {
    let rootIsBeam = false;
    for (let j = 0; j < curCount; j++) if (cur.node[j] === root) { rootIsBeam = true; break; }
    if (rootIsBeam) break;
    const child = pscr[idx];
    pushEmitted(state, state.tToken[child]);
    state.edge.delete(root * edgeStride + state.tToken[child]);
    state.tFreed[root] = 1;
    state.treeFree.push(root);
    state.tParent[child] = -1;
    root = child;
  }
  state.root = root;
}

// ════════════════════════════════ public API ════════════════════════════════

// Create the empty decoding state — the opaque handle threaded through
// ctcBeamStep → ctcBeamFinalize. Per-session scratch/pools are sized lazily; all
// fields are declared up front so the object stays one hidden class.
export function initialBeamState(trie = null) {
  const cur = makeBeams(16);
  cur.node[0] = 0;
  cur.logPb[0] = 0.0;
  cur.logPnb[0] = NEG_INF;
  cur.logLm[0] = 0.0;
  cur.lastToken[0] = -1;
  cur.trieNode[0] = trie ? 0 : -1;
  cur.committed[0] = -1;
  cur.curSum[0] = 0.0;
  cur.curCount[0] = 0;
  cur.wh0[0] = 0; cur.wh1[0] = 0; cur.whLen[0] = 0;
  // Empty-hypothesis score. The prune floor reads it on frame 0 only when
  // beamWidth === 1 (the beam saturates immediately); otherwise defensive —
  // compactSurvivors rewrites cur.score before the beam can saturate.
  cur.score[0] = 0.0;

  return {
    cur, next: null, curCount: 1,
    // token tree (node 0 = permanent root; freed nodes recycle via treeFree)
    tParent: Int32Array.of(-1), tToken: Int32Array.of(-1),
    tBeamSlot: Int32Array.of(-1), tBeamStamp: Int32Array.of(-1), // beam row / frame occupying a node
    tChildCount: Int32Array.of(0), tLiveStamp: Int32Array.of(-1), tFreed: Uint8Array.of(0),
    tCount: 1, tCap: 1, treeFree: [],
    edge: new Map(), edgeStride: 0,           // (parent*edgeStride + token) → child node
    // root shifting: settled prefix tokens are evicted to `emitted`
    root: 0, emitted: new Int32Array(64), emittedLen: 0, pathScratch: new Int32Array(64),
    // committed-word cons pool (refcounted)
    cellSum: new Float32Array(0), cellCount: new Int32Array(0),
    cellPrev: new Int32Array(0), cellRef: new Int32Array(0),
    cellTop: 0, cellCap: 0, cellFree: [],
    // resolved per-call config + flat trie (set in ctcBeamStep)
    V: 0, blankId: 0, beamWidth: 0, topK: 0, useLm: false,
    lmAlpha: 0, lmBeta: 0, ngramLm: null, ngramContextLen: 0, pruneSafety: Infinity,
    // Diagnostics: cumulative count of V-scan entries short-circuited by the
    // candidate-prune floor. Tests use it to prove parity checks aren't vacuous.
    prunedTokens: 0,
    flatTrie: null, _trieRef: null, frameLogits: null, frameMax: 0,
    // frame scratch (sized lazily per V / topK / beamWidth)
    sizedV: 0, sizedTopK: 0, sizedBeamWidth: 0,
    isWordInitial: null, seen: null, candidates: null, topScores: null, topIds: null,
    topIdx: null, survMark: null, created: [], frameStamp: 0,
  };
}

// Reset an existing state to the empty hypothesis, reusing every pooled buffer
// (beam columns, token tree, cons-cell pool, edge map, frame scratch) instead of
// allocating a new one. For a caller that decodes many independent windows — e.g.
// a rolling-window streamer running a fresh beam every stride — this turns the
// per-call `initialBeamState()` allocation into a one-time cost. The resulting
// decode is identical to a fresh state (locked by a parity test in
// ctc_beam.test.js); only logical decode state is cleared, capacities are kept.
//
// frameStamp is deliberately NOT reset: stamps only need to stay monotonic to
// mark "this frame", and letting it keep advancing avoids any collision with a
// stale stamp left on a recycled tree node. The per-call config (V, blankId,
// LM params, pruneSafety) and the flat trie are (re)set by ctcBeamStep, so they
// are not touched here. The state must keep the same vocab size V it was sized
// for; ctcBeamStep still enforces that.
export function resetBeamState(state, trie = null) {
  const cur = state.cur;
  cur.node[0] = 0;
  cur.logPb[0] = 0.0;
  cur.logPnb[0] = NEG_INF;
  cur.logLm[0] = 0.0;
  cur.lastToken[0] = -1;
  cur.trieNode[0] = trie ? 0 : -1;
  cur.committed[0] = -1;
  cur.curSum[0] = 0.0;
  cur.curCount[0] = 0;
  cur.wh0[0] = 0; cur.wh1[0] = 0; cur.whLen[0] = 0;
  cur.score[0] = 0.0; // seeds the candidate-prune floor on frame 0
  state.curCount = 1;

  // token tree → root only (node 0 permanent; nodes ≥1 recycle on next decode,
  // each fully reinitialized at allocation time in expandBeams)
  state.tParent[0] = -1; state.tToken[0] = -1;
  state.tBeamSlot[0] = -1; state.tBeamStamp[0] = -1;
  state.tChildCount[0] = 0; state.tLiveStamp[0] = -1; state.tFreed[0] = 0;
  state.tCount = 1;
  state.treeFree.length = 0;
  state.root = 0;
  state.edge.clear();

  // evicted-prefix output + committed-word cons pool (buffers kept, counts zeroed)
  state.emittedLen = 0;
  state.cellTop = 0;
  state.cellFree.length = 0;
  state.prunedTokens = 0;

  // Switching to a no-trie decode: drop the cached flat trie so the reset
  // doesn't pin the last passage's trie buffers for the engine's lifetime.
  // (Kept when a trie is passed — ctcBeamStep re-flattens only on change, and
  // the realtime path passes the same trie every stride.)
  if (!trie) { state.flatTrie = null; state._trieRef = null; }

  return state;
}

function ensureSized(state, V, topK, beamWidth, vocab) {
  if (state.next && state.sizedV === V && state.sizedTopK === topK && state.sizedBeamWidth === beamWidth) return;
  state.sizedV = V; state.sizedTopK = topK; state.sizedBeamWidth = beamWidth;
  state.next = makeBeams(beamWidth * (topK + 32) + 128); // headroom; growBeams is the backstop
  state.survMark = new Int32Array(state.next.cap);
  state.candidates = new Int32Array(V);
  state.topScores = new Float32Array(topK);
  state.topIds = new Int32Array(topK);
  state.topIdx = new Int32Array(beamWidth);
  state.seen = new Uint8Array(V);
  state.edgeStride = V + 1; // stride for (parent, token) edge keys; token < V, +1 is harmless headroom
  if (!state.isWordInitial || state.isWordInitial.length !== V) {
    state.isWordInitial = new Uint8Array(V);
    if (vocab) {
      for (let v = 0; v < V; v++) {
        if (vocab[v] && vocab[v].charCodeAt(0) === 0x2581) state.isWordInitial[v] = 1;
      }
    }
  }
}

// Advance the decode over T frames of logits[0 .. T*V]. Returns the same state.
export function ctcBeamStep(logits, T, V, blankId, state, opts = {}) {
  const trie = opts.trie ?? null;
  const vocab = opts.vocab ?? null;
  state.V = V;
  state.blankId = blankId;
  state.lmAlpha = opts.lmAlpha ?? -3.0;
  state.lmBeta = opts.lmBeta ?? 1.0;
  state.ngramLm = opts.ngramLm ?? null;
  state.ngramContextLen = state.ngramLm?._contextLen ?? 0;
  state.beamWidth = opts.beamWidth ?? (trie ? 4 : 8);
  state.topK = opts.topK ?? (trie ? 30 : 50);
  state.useLm = !!(trie && vocab);
  // Admissible candidate prune margin in nats (see collectCandidates). 18 keeps
  // decoded tokens identical and word scores within ~1e-7; Infinity disables it.
  // Fail safe rather than lossy: the floor's maxLmBonus bound needs every LM
  // term to be a penalty — lmAlpha ≤ 0 and ngramLm.score() ≤ 0, i.e. weight ≥ 0
  // AND smoothing ≥ 0 (negative smoothing can push a conditional above 1, so
  // logP > 0) — and token exactness needs a margin above log(beamWidth) (prefix
  // merges). Any config outside that envelope turns the prune off entirely
  // rather than silently changing decodes.
  const lmAdmissible = !state.useLm ||
    (state.lmAlpha <= 0 &&
      (state.ngramLm == null || (state.ngramLm.weight >= 0 && state.ngramLm.smoothing >= 0)));
  const margin = opts.pruneSafety ?? 18;
  state.pruneSafety = lmAdmissible && margin > Math.log(state.beamWidth) ? margin : Infinity;
  state.frameLogits = logits;

  // A state is bound to one vocab size for life: the token tree, edge keys
  // (bNode*edgeStride + token) and the flat trie are all sized for V. Changing it
  // would silently corrupt dedup/LM, so refuse — use a fresh state per model.
  if (state.sizedV !== 0 && state.sizedV !== V) {
    throw new Error(`ctcBeamStep: vocab size changed (${state.sizedV} → ${V}); use a fresh state per model.`);
  }
  ensureSized(state, V, state.topK, state.beamWidth, vocab);
  if (trie && state._trieRef !== trie) { state.flatTrie = flattenTrie(trie, V); state._trieRef = trie; }

  // Gaussian frame weight: down-weight edge frames (less acoustic context) when
  // scoring per-word confidence. σ = T/3.
  const gaussCenter = (T - 1) / 2;
  const gaussSigmaSq = Math.pow(T / 3, 2);

  for (let t = 0; t < T; t++) {
    const off = t * V;
    const gauss = -0.5 * Math.pow(t - gaussCenter, 2) / gaussSigmaSq;
    const stamp = ++state.frameStamp;

    const nCand = collectCandidates(state, logits, off);
    const blankLp = frameLogProb(logits, off + blankId, state.frameMax);

    ensureFrameCapacity(state, state.curCount * (nCand + 2));
    const nextCount = expandBeams(state, logits, off, nCand, gauss, blankLp, stamp);
    const topN = selectTopB(state, nextCount);
    pruneFrame(state, nextCount, topN, stamp);
    compactSurvivors(state, topN);
    evictStablePrefix(state);
  }
  // The caller may free `logits` right after this step (engine.js hands us a
  // live ORT tensor view and disposes it before the next run) — drop the
  // reference so nothing can later read a dead buffer through the state.
  state.frameLogits = null;
  return state;
}

// Extract the best token sequence and per-word confidence scores (in (0, 1]).
// tokens = evicted stable prefix ++ suffix walked from the best leaf to the root;
// word scores = exp(meanWeightedLogProb) per committed word + the in-progress one.
//
// MUST be read-only: createSession (engine.js) calls this after every chunk and
// then keeps stepping the same state, so any mutation here would corrupt
// incremental decoding.
export function ctcBeamFinalize(state) {
  if (state.curCount === 0) return { tokens: new Int32Array(0), wordScores: new Float32Array(0) };
  const cur = state.cur;
  const best = 0; // survivors are stored in rank order; cur[0] is the top beam
  const bestNode = cur.node[best];

  let suffix = 0;
  for (let n = bestNode; n !== state.root; n = state.tParent[n]) suffix++;
  const total = state.emittedLen + suffix;
  if (total === 0) return { tokens: new Int32Array(0), wordScores: new Float32Array(0) };

  const tokens = new Int32Array(total);
  if (state.emittedLen > 0) tokens.set(state.emitted.subarray(0, state.emittedLen), 0);
  let i = total;
  for (let n = bestNode; n !== state.root; n = state.tParent[n]) tokens[--i] = state.tToken[n];

  let nWords = 0;
  for (let c = cur.committed[best]; c !== -1; c = state.cellPrev[c]) nWords++;
  const hasCurrent = cur.curCount[best] > 0;
  const wordScores = new Float32Array(nWords + (hasCurrent ? 1 : 0));
  let w = nWords;
  for (let c = cur.committed[best]; c !== -1; c = state.cellPrev[c]) {
    wordScores[--w] = state.cellCount[c] > 0 ? Math.exp(state.cellSum[c] / state.cellCount[c]) : 0;
  }
  if (hasCurrent) wordScores[nWords] = Math.exp(cur.curSum[best] / cur.curCount[best]);

  return { tokens, wordScores };
}

// Convenience wrapper: single-pass decode over a pre-stitched logit buffer.
export function ctcBeamSearch(logits, T, V, blankId, opts = {}) {
  const state = ctcBeamStep(logits, T, V, blankId, initialBeamState(opts.trie ?? null), opts);
  return ctcBeamFinalize(state);
}
